import { Link } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { BarChart3, Shield, Zap, Globe, TrendingUp, Users } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { AUTH_SERVICE_URL } from '../lib/constants'

export default function Landing() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  const handleGetStarted = () => {
    if (isAuthenticated) {
      window.location.href = '/dashboard'
    } else {
      window.location.href = `${AUTH_SERVICE_URL}/auth?redirect_uri=${encodeURIComponent(
        window.location.origin + '/dashboard'
      )}`
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            <span className="text-xl font-bold">Analytics</span>
          </div>
          <nav className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button>Go to Dashboard</Button>
              </Link>
            ) : (
              <Button onClick={handleGetStarted}>Get Started</Button>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-5xl font-bold tracking-tight mb-6">
          Privacy-First Web Analytics
        </h1>
        <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
          Simple, fast, and privacy-focused analytics for your websites. No cookies, no tracking, just insights.
        </p>
        <Button size="lg" onClick={handleGetStarted}>
          Start Tracking Now
        </Button>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12">
          Everything you need to understand your audience
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <Zap className="h-10 w-10 text-primary mb-2" />
              <CardTitle>Real-time Analytics</CardTitle>
              <CardDescription>
                See your visitors in real-time with WebSocket-powered updates
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <Shield className="h-10 w-10 text-primary mb-2" />
              <CardTitle>Privacy Focused</CardTitle>
              <CardDescription>
                No cookies, no PII collection. GDPR compliant by default
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <Globe className="h-10 w-10 text-primary mb-2" />
              <CardTitle>Multi-Site Management</CardTitle>
              <CardDescription>
                Track multiple websites from a single, unified dashboard
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <TrendingUp className="h-10 w-10 text-primary mb-2" />
              <CardTitle>A/B Testing Support</CardTitle>
              <CardDescription>
                Built-in A/B testing tracking and conversion analysis
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <Users className="h-10 w-10 text-primary mb-2" />
              <CardTitle>Simple Integration</CardTitle>
              <CardDescription>
                One line of code to start tracking. Works everywhere
              </CardDescription>
            </CardHeader>
          </Card>

          <Card>
            <CardHeader>
              <BarChart3 className="h-10 w-10 text-primary mb-2" />
              <CardTitle>Beautiful Reports</CardTitle>
              <CardDescription>
                Clean, intuitive dashboards with all the metrics you need
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* Integration Example */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Get started in seconds
          </h2>
          <Card>
            <CardHeader>
              <CardTitle>Add this script to your website</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted p-4 rounded-md overflow-x-auto">
                <code className="text-sm">
                  {`<script defer 
  src="https://analytics.omattic.com/stats/a.js"
  data-site="yoursite.com">
</script>`}
                </code>
              </pre>
              <p className="text-sm text-muted-foreground mt-4">
                That's it! Your analytics will start appearing in your dashboard immediately.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-4xl font-bold mb-6">
          Ready to get started?
        </h2>
        <p className="text-xl text-muted-foreground mb-8">
          Create your account and start tracking in under 60 seconds
        </p>
        <Button size="lg" onClick={handleGetStarted}>
          Get Started For Free
        </Button>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© 2024 Analytics. Part of the Omattic suite.</p>
        </div>
      </footer>
    </div>
  )
}
